import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Switch,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import { Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
export default function AddUserModel({
  open,
  onClose,
  onSubmit,
  initialData = null,
}) {
  const isEdit = Boolean(initialData);
  const roleOptions = [
    { value: "user", label: "Platform User" },
    { value: "superadmin", label: "Superadmin" },
  ];

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    global_role: "user",
    status: "active",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
     if (!open) return;
    if (initialData) {
      setForm({
        name: initialData.name || "",
        email: initialData.email || "",
        password: "",
        global_role: initialData.global_role || "user",
        status: initialData.status || "active",
      });
      return;
    }

    setForm({
      name: "",
      email: "",
      password: "",
      global_role: "user",
      status: "active",
    });
    setErrors({
      name: "",
      email: "",
      password: "",
    });
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "active" : "suspended") : value,
    }));
  };

  const handleSubmit = async () => {
    let newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Full Name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    }

    if (!isEdit && !form.password.trim()) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setSaving(true);

      await onSubmit(form); // make sure parent returns promise
    } finally {
      setSaving(false);
    }
  };
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
  });
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2.5,
          py: 2,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
          {isEdit ? "Update Platform User" : "Create Platform User"}
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.08)",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.18)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Full Name"
            name="name"
            value={form.name}
            onChange={(e) => {
              handleChange(e);
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            fullWidth
            required
            disabled={isEdit}
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label="Email"
            name="email"
            value={form.email}
            onChange={(e) => {
              handleChange(e);
              setErrors((prev) => ({ ...prev, email: "" }));
            }}
            fullWidth
            required
            disabled={isEdit}
            error={!!errors.email}
            helperText={errors.email}
          />

          {!isEdit && (
            <>
              <TextField
                label="Temporary Password"
                name="password"
                type="password"
                value={form.password}
                onChange={(e) => {
                  handleChange(e);
                  setErrors((prev) => ({ ...prev, password: "" }));
                }}
                fullWidth
                required
                error={!!errors.password}
                helperText={
                  errors.password ||
                  "Use at least 8 characters for newly created Firebase users."
                }
              />
              <Typography variant="body2" color="text.secondary">
                If this email already exists in Firebase, the platform will just
                link that account here and the password will be ignored.
              </Typography>
            </>
          )}

          {isEdit && (
            <>
              <TextField
                label="New Password"
                name="password"
                type="password"
                value={form.password}
                onChange={(e) => {
                  handleChange(e);
                  setErrors((prev) => ({ ...prev, password: "" }));
                }}
                fullWidth
                error={!!errors.password}
                helperText={
                  errors.password || "Leave blank to keep current password."
                }
              />
              <Typography variant="body2" color="text.secondary">
                This updates the user's Firebase sign-in password immediately.
              </Typography>
            </>
          )}

          <TextField
            select
            label="Global Role"
            name="global_role"
            value={form.global_role}
            onChange={handleChange}
            fullWidth
          >
            {roleOptions.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={form.status === "active"}
                onChange={handleChange}
                name="status"
              />
            }
            label={form.status === "active" ? "Active" : "Suspended"}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
             minWidth: 100,
            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
          
            "&:hover": {
              background: "linear-gradient(135deg, #FCD34D, #D97706)",
            },
          }}
        >
          {saving ? (
            <CircularProgress size={22} />
          ) : isEdit ? (
            "Update User"
          ) : (
            "Create User"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
